import { useNotification } from "@context/NotificationContext";
import { drybulkCargoOperationsService } from "@services/drybulkCargoOperationsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import {
  DRYBULK_CARGO_OPS_LANG_BASE,
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  DRYBULK_CARGO_OPS_TELEMETRY,
} from "../constants";
import { problemDetail, problemStatus } from "../utils/problemDetail";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.delete`;

/**
 * DBCO-0065 - soft-deletes a Draft from the operations grid, one business transaction (the telemetry id is
 * forwarded to the API call). On success the operation's cached detail is dropped and the grid refreshed; a 409
 * (no longer a Draft) and a 404 (gone already) refresh the grid and say so; any other failure is the standard
 * error notice with the server's detail. The Add page reuses it (DBCO-0065 D17) with its own telemetry screen and
 * success message.
 */
export const useDeleteCargoOperation = ({
  screen = DRYBULK_CARGO_OPS_TELEMETRY.gridScreen,
  successKey = `${L}.success`,
}: { screen?: string; successKey?: string } = {}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const refreshGrid = () =>
    queryClient.invalidateQueries({ queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.searchAll() });

  return useMutation({
    mutationFn: (operationId: string) =>
      runTransaction(
        {
          module: DRYBULK_CARGO_OPS_TELEMETRY.module,
          screen,
          action: "delete_cargo_operation",
          entityType: "cargo_operation",
          entityId: operationId,
        },
        (transactionId) =>
          drybulkCargoOperationsService.deleteOperation(operationId, { transactionId }),
      ),
    onSuccess: (_result, operationId) => {
      queryClient.removeQueries({ queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.detail(operationId) });
      void refreshGrid();
      notify({ type: "success", title: t(successKey) });
    },
    onError: (error) => {
      const status = problemStatus(error);
      if (status === 409) {
        void refreshGrid();
        notify({ type: "warning", title: t(`${L}.conflict`) });
        return;
      }
      if (status === 404) {
        void refreshGrid();
        notify({ type: "info", title: t(`${L}.gone`) });
        return;
      }
      notify({
        type: "error",
        title: t("common.error"),
        description: problemDetail(error) ?? t("common.error"),
      });
    },
  });
};
